import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { getMonthlyReport, MonthlySalesReport } from '../../services/reportService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ReportsDashboard: React.FC = () => {
  const [monthlyReports, setMonthlyReports] = useState<MonthlySalesReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'6months' | '12months'>('6months');

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        await fetchReportsData();
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [selectedPeriod]);

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const monthsToFetch = selectedPeriod === '6months' ? 6 : 12;
      
      const reports: MonthlySalesReport[] = [];
      
      // Fetch monthly reports for the last N months
      for (let i = monthsToFetch - 1; i >= 0; i--) {
        const targetDate = new Date(endDate.getFullYear(), endDate.getMonth() - i, 1);
        const year = targetDate.getFullYear();
        const month = targetDate.getMonth() + 1;
        
        try {
          const report = await getMonthlyReport({ year, month });
          reports.push(report);
        } catch (err) {
          console.error(`Failed to fetch report for ${year}-${month}:`, err);
          // Push empty data for failed months
          reports.push({
            year,
            month,
            month_name: targetDate.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' }),
            total_transactions: 0,
            total_items_sold: 0,
            avg_transactions_per_day: 0,
            gross_sales: 0,
            total_discount: 0,
            net_sales: 0,
            total_cost: 0,
            gross_profit: 0,
            profit_margin: 0,
            avg_daily_sales: 0,
            best_day_sales: 0,
            worst_day_sales: 0,
            payment_breakdown: { cash: 0, transfer: 0, qris: 0, edc: 0 },
            top_items: [],
            top_customers: [],
          });
        }
      }
      
      setMonthlyReports(reports);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch reports data');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Chart data for monthly sales
  const monthlyChartData = {
    labels: monthlyReports.map(d => d.month_name),
    datasets: [
      {
        label: 'Pendapatan (Rp)',
        data: monthlyReports.map(d => d.net_sales),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        yAxisID: 'y',
      },
      {
        label: 'Keuntungan (Rp)',
        data: monthlyReports.map(d => d.gross_profit),
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 2,
        yAxisID: 'y',
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Grafik Penjualan Bulanan',
        font: {
          size: 18,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
              }).format(context.parsed.y);
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        ticks: {
          callback: function(value: any) {
            return 'Rp ' + new Intl.NumberFormat('id-ID').format(value);
          },
        },
      },
    },
  };

  // Transactions chart
  const transactionsChartData = {
    labels: monthlyReports.map(d => d.month_name),
    datasets: [
      {
        label: 'Jumlah Transaksi',
        data: monthlyReports.map(d => d.total_transactions),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.5)',
        tension: 0.4,
      },
    ],
  };

  const transactionsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Jumlah Transaksi Bulanan',
        font: {
          size: 18,
          weight: 'bold' as const,
        },
      },
    },
  };

  // Get all top items across all months and aggregate
  const allItems = new Map<number, any>();
  monthlyReports.forEach(report => {
    report.top_items?.forEach(item => {
      if (allItems.has(item.item_id)) {
        const existing = allItems.get(item.item_id);
        existing.qty_sold += item.qty_sold;
        existing.total_revenue += item.total_revenue;
        existing.total_profit += item.total_profit;
      } else {
        allItems.set(item.item_id, { ...item });
      }
    });
  });

  // Sort by quantity sold and take top 10
  const bestSellingItems = Array.from(allItems.values())
    .sort((a, b) => b.qty_sold - a.qty_sold)
    .slice(0, 10)
    .map((item, index) => ({
      ...item,
      rank: index + 1,
      profit_margin: item.total_revenue > 0 ? (item.total_profit / item.total_revenue * 100) : 0,
    }));

  // Get all top customers across all months and aggregate
  const allCustomers = new Map<string, any>();
  monthlyReports.forEach(report => {
    report.top_customers?.forEach(customer => {
      const key = `${customer.customer_name}_${customer.customer_phone || 'no-phone'}`;
      if (allCustomers.has(key)) {
        const existing = allCustomers.get(key);
        existing.total_transactions += customer.total_transactions;
        existing.total_spent += customer.total_spent;
        existing.total_items_purchased += customer.total_items_purchased;
      } else {
        allCustomers.set(key, { ...customer });
      }
    });
  });

  // Sort by total spent and take top 5
  const topCustomers = Array.from(allCustomers.values())
    .sort((a, b) => b.total_spent - a.total_spent)
    .slice(0, 5);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <div className="text-xl text-gray-700">Memuat data laporan...</div>
        <p className="text-sm text-gray-500">Mengambil data dari server</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-red-600 text-xl">Error: {error}</div>
        <button
          onClick={() => {
            setError(null);
            fetchReportsData();
          }}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Coba Lagi
        </button>
        <p className="text-sm text-gray-500 max-w-md text-center">
          Pastikan backend API sudah berjalan dan dapat diakses.
          Periksa juga apakah Cloudflare Tunnel sudah aktif.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">Dashboard Laporan</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedPeriod('6months')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === '6months'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            6 Bulan
          </button>
          <button
            onClick={() => setSelectedPeriod('12months')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPeriod === '12months'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            12 Bulan
          </button>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Sales Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div style={{ height: '400px' }}>
            <Bar data={monthlyChartData} options={monthlyChartOptions} />
          </div>
        </div>

        {/* Transactions Chart */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div style={{ height: '400px' }}>
            <Line data={transactionsChartData} options={transactionsChartOptions} />
          </div>
        </div>
      </div>

      {/* Tables Grid - Best Selling Items and Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Best Selling Items Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Rating Barang Terlaris (Top 10)
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nama Barang
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Terjual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pendapatan
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bestSellingItems.map((item) => (
                  <tr 
                    key={item.item_sku}
                    className={`hover:bg-gray-50 ${
                      item.rank <= 3 ? 'bg-yellow-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm ${
                            item.rank === 1
                              ? 'bg-yellow-400 text-yellow-900'
                              : item.rank === 2
                              ? 'bg-gray-300 text-gray-900'
                              : item.rank === 3
                              ? 'bg-orange-300 text-orange-900'
                              : 'bg-blue-100 text-blue-900'
                          }`}
                        >
                          {item.rank}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{item.item_name}</div>
                      <div className="text-xs text-gray-500">{item.item_sku}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {item.qty_sold}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                      {formatCurrency(item.total_revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {bestSellingItems.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Tidak ada data barang terlaris untuk periode ini.
            </div>
          )}
        </div>

        {/* Top Customers Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Top 5 Customer Pembelian Terbanyak
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaksi
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Belanja
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topCustomers.map((customer, index) => (
                  <tr 
                    key={`${customer.customer_name}-${customer.customer_phone || index}`}
                    className={`hover:bg-gray-50 ${
                      index < 3 ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <span
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm ${
                            index === 0
                              ? 'bg-yellow-400 text-yellow-900'
                              : index === 1
                              ? 'bg-gray-300 text-gray-900'
                              : index === 2
                              ? 'bg-orange-300 text-orange-900'
                              : 'bg-blue-100 text-blue-900'
                          }`}
                        >
                          {index + 1}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="font-medium">{customer.customer_name}</div>
                      {customer.customer_phone && (
                        <div className="text-xs text-gray-500">{customer.customer_phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                      {customer.total_transactions}x
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                      {formatCurrency(customer.total_spent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {topCustomers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Tidak ada data customer untuk periode ini.
            </div>
          )}
        </div>
      </div>

      {/* Original Best Selling Items Table - Remove this section */}
      {/* Best Selling Items Table */}
      <div className="bg-white rounded-lg shadow-md p-6 hidden">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Rating Barang Terlaris (Top 10)
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nama Barang
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Terjual
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Pendapatan
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margin (%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bestSellingItems.map((item) => (
                <tr 
                  key={item.item_sku}
                  className={`hover:bg-gray-50 ${
                    item.rank <= 3 ? 'bg-yellow-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                          item.rank === 1
                            ? 'bg-yellow-400 text-yellow-900'
                            : item.rank === 2
                            ? 'bg-gray-300 text-gray-900'
                            : item.rank === 3
                            ? 'bg-orange-300 text-orange-900'
                            : 'bg-blue-100 text-blue-900'
                        }`}
                      >
                        {item.rank}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.item_sku}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.item_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                    {item.qty_sold}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                    {formatCurrency(item.total_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span
                      className={`font-semibold ${
                        (item.profit_margin ?? 0) >= 30
                          ? 'text-green-600'
                          : (item.profit_margin ?? 0) >= 15
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}
                    >
                      {(item.profit_margin ?? 0).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {bestSellingItems.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Tidak ada data barang terlaris untuk periode ini.
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
          <h3 className="text-gray-500 text-sm font-medium mb-2">Total Pendapatan</h3>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(monthlyReports.reduce((sum, d) => sum + d.net_sales, 0))}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
          <h3 className="text-gray-500 text-sm font-medium mb-2">Total Keuntungan</h3>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(monthlyReports.reduce((sum, d) => sum + d.gross_profit, 0))}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
          <h3 className="text-gray-500 text-sm font-medium mb-2">Total Transaksi</h3>
          <p className="text-3xl font-bold text-gray-900">
            {monthlyReports.reduce((sum, d) => sum + d.total_transactions, 0).toLocaleString('id-ID')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboard;
